import React from "react";
import {useFormik} from "formik";
import * as yup from "yup";
import {useTranslation} from "react-i18next";

import MDLoadingButton from "../../../components/MDLoadingButton";

import MDBox from "materialDashboard/components/MDBox";
import MDTypography from "materialDashboard/components/MDTypography";

import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ProvinceAutocomplete from "../../../components/inputs/ProvinceAutocomplete";
import MunicipalityAutocomplete from "../../../components/inputs/MunicipalityAutocomplete";
import LocaleAutocomplete from "../../../components/inputs/LocaleAutocomplate";


const CardContact = ({user, userMutation}) => {
    const {t} = useTranslation(['user', "validation"]);

    const formik = useFormik({
        initialValues: {
            phone1: user.phone1 ?? "",
            phone2: user.phone2 ?? "",
            phone3: user.phone3 ?? "",
            address: user.address ?? "",
            municipalityId: user.municipalityId ?? null,
            provinceId: user.provinceId ?? null,
            defaultLocale: user.defaultLocale ?? null,
        },
        validationSchema: yup.object({
            phone1: yup.string(),
            phone2: yup.string(),
            phone3: yup.string(),
            address: yup.string(),
        }),
        onSubmit: async (values) => {
            try {
                await userMutation.mutateAsync(values);
                formik.resetForm({values});  // reset form
            } catch (e) {
                // Do nothing because the error is already handled by the FeedbackProvider
            }
        }
    });


    const handleProvinceChange = (provinceId) => {
        formik.setFieldValue('provinceId', provinceId ?? null);
        formik.setFieldValue('municipalityId', null);
    }

    const disableSave = !formik.dirty;

    return <Card>
        <MDBox py={2} px={2}>
            <MDTypography variant="h5" fontWeight="medium" textTransform="capitalize">
                {t('contactCard.title')}
            </MDTypography>
            <Stack spacing={2} mt={2} alignItems="center">
                <TextField name="phone1"
                           label={t('contactCard.phone1')}
                           value={formik.values.phone1}
                           onChange={formik.handleChange}
                           error={formik.touched.phone1 && Boolean(formik.errors.phone1)}
                           helperText={formik.touched.phone1 && formik.errors.phone1}
                           variant="standard"
                           fullWidth
                />
                <TextField name="phone2"
                           label={t('contactCard.phone2')}
                           value={formik.values.phone2}
                           onChange={formik.handleChange}
                           error={formik.touched.phone2 && Boolean(formik.errors.phone2)}
                           helperText={formik.touched.phone2 && formik.errors.phone2}
                           variant="standard"
                           fullWidth
                />
                <TextField name="phone3"
                           label={t('contactCard.phone3')}
                           value={formik.values.phone3}
                           onChange={formik.handleChange}
                           error={formik.touched.phone3 && Boolean(formik.errors.phone3)}
                           helperText={formik.touched.phone3 && formik.errors.phone3}
                           variant="standard"
                           fullWidth
                />
                <TextField name="address"
                           label={t('contactCard.address')}
                           value={formik.values.address}
                           onChange={formik.handleChange}
                           error={formik.touched.address && Boolean(formik.errors.address)}
                           helperText={formik.touched.address && formik.errors.address}
                           variant="standard"
                           fullWidth
                />
                <ProvinceAutocomplete name="provinceId"
                                      value={formik.values.provinceId}
                                      onChange={handleProvinceChange}
                                      MDInputProps={{
                                          name: 'provinceId',
                                          label: t('contactCard.provinceId'),
                                          error: formik.touched.provinceId && Boolean(formik.errors.provinceId),
                                          helperText: formik.touched.provinceId && formik.errors.provinceId
                                      }}
                />
                <MunicipalityAutocomplete name="municipalityId"
                                          provinceId={formik.values.provinceId}
                                          value={formik.values.municipalityId}
                                          onChange={(municipalityId) => formik.setFieldValue('municipalityId', municipalityId)}
                                          MDInputProps={{
                                              name: 'municipalityId',
                                              error: formik.touched.municipalityId && Boolean(formik.errors.municipalityId),
                                              helperText: formik.touched.municipalityId && formik.errors.municipalityId
                                          }}/>
                <LocaleAutocomplete name="defaultLocale"
                                    fullWidth
                                    value={formik.values.defaultLocale}
                                    onChange={formik.handleChange}
                                    error={formik.touched.defaultLocale && Boolean(formik.errors.defaultLocale)}
                                    helperText={formik.touched.defaultLocale && formik.errors.defaultLocale}/>
                <MDBox>
                    <MDLoadingButton variant="contained"
                                     color="dark"
                                     loading={userMutation.isLoading}
                                     onClick={formik.handleSubmit}
                                     disabled={disableSave}>
                        {t('cardBasicInfo.submit')}
                    </MDLoadingButton>
                </MDBox>
            </Stack>
        </MDBox>
    </Card>
}

export default CardContact;